import React, { useCallback, useMemo, useState } from "react";
import { SovosBox, SovosButton, SovosTypography } from "mosaic-react";
import { AutoAwesome, Close, ExpandMore, FilterList } from "mosaic-react-icons";

const AI_ACCENT = "secondary.main";
const AI_ACCENT_BG = "secondary.light";
const RULE_DETAILS_BG = "grey.100";
const PANEL_WIDTH = 395;

const SKIP_STATUSES = new Set(["skipped_non_deterministic", "skipped_non_nullable", "skipped_bad_format"]);

function deriveErrorColumns(issueSummary) {
    if (!issueSummary || typeof issueSummary !== "object") return [];
    const byColumn = issueSummary.by_column;
    if (!byColumn || typeof byColumn !== "object") return [];
    return Object.entries(byColumn)
        .filter(([, v]) => (v.errors ?? 0) > 0)
        .sort(([, a], [, b]) => (b.errors ?? 0) - (a.errors ?? 0))
        .map(([col]) => col);
}

function buildRules(aiFixes, acceptedFixes, rejectedFixes) {
    if (!aiFixes?.suggestions) return [];
    return aiFixes.suggestions.map((col, idx) => {
        const visibleFixes = (col.fixes || []).filter(
            (f) => !rejectedFixes.has(`${f.clb_id}:${col.column_name}`),
        );
        const pendingFixes = visibleFixes.filter(
            (f) => f.status === "applied" && !acceptedFixes.has(`${f.clb_id}:${col.column_name}`),
        );
        const exampleFix = visibleFixes.find((f) => f.status === "applied") ?? null;
        const isManual = visibleFixes.every((f) => SKIP_STATUSES.has(f.status));
        const confidence = col.confidence != null ? Math.round(col.confidence * 100) : null;
        const totalAffected = col.total_affected ?? visibleFixes.length;

        return {
            index: idx + 1,
            columnName: col.column_name,
            confidence,
            exampleFix,
            isManual,
            pendingFixes,
            visibleFixes,
            totalAffected,
            rationale: col.rationale,
            sampled: col.sampled || [],
        };
    }).filter((r) => r.visibleFixes.length > 0);
}

const ConfidenceBadge = ({ confidence }) => {
    if (confidence == null) return null;
    const color = confidence >= 80 ? AI_ACCENT : confidence >= 60 ? "warning.dark" : "text.secondary";
    const bg = confidence >= 80 ? AI_ACCENT_BG : confidence >= 60 ? "warning.light" : "grey.100";
    return (
        <SovosBox
            sx={{
                display: "inline-flex",
                alignItems: "center",
                height: 24,
                px: 1,
                borderRadius: 999,
                bgcolor: bg,
            }}
        >
            <SovosTypography
                variant="caption"
                sx={{ fontSize: 12, fontWeight: 600, color, lineHeight: 1 }}
            >
                {confidence}% confidence
            </SovosTypography>
        </SovosBox>
    );
};

function buildPseudoRule(rule) {
    if (!rule.exampleFix) {
        return [
            `Review staging values in column "${rule.columnName}"`,
            "Apply the suggested normalization to affected rows only",
            "Scope is limited to this import profile staging data",
        ].join("\n");
    }

    return [
        `Target column: ${rule.columnName}`,
        `Replace "${rule.exampleFix.original_value ?? "empty"}" with "${rule.exampleFix.new_value ?? "empty"}"`,
        `Apply to ${rule.pendingFixes.length || rule.totalAffected} affected staging row${(rule.pendingFixes.length || rule.totalAffected) !== 1 ? "s" : ""} only`,
    ].join("\n");
}

const RuleCard = ({ rule, expanded, onApprove, onReject, onToggle, acceptedFixes }) => {
    const allApproved = rule.pendingFixes.length === 0 &&
        rule.visibleFixes.some((f) => acceptedFixes.has(`${f.clb_id}:${rule.columnName}`));
    const applicableFixCount = rule.pendingFixes.length;
    const pseudoRule = buildPseudoRule(rule);

    const affectingCount = allApproved ? rule.visibleFixes.length : applicableFixCount;

    return (
        <SovosBox
            sx={{
                bgcolor: "common.white",
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
                overflow: "hidden",
                boxShadow: 1,
            }}
        >
            <SovosBox
                onClick={onToggle}
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    px: 2,
                    pt: 2,
                    pb: 2,
                    cursor: "pointer",
                    userSelect: "none",
                    "&:hover": { bgcolor: "grey.50" },
                }}
            >
                <SovosBox
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        width: "100%",
                        height: 32,
                    }}
                >
                    <SovosTypography
                        variant="subtitle2"
                        fontWeight={700}
                        sx={{ fontSize: 16, lineHeight: "28px", color: "text.primary" }}
                    >
                        Rule #{rule.index}
                    </SovosTypography>
                    <ConfidenceBadge confidence={rule.confidence} />
                    <SovosBox sx={{ ml: "auto", display: "flex", alignItems: "center" }}>
                        <SovosBox
                            sx={{
                                width: 32,
                                height: 32,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                borderRadius: 1,
                                color: "text.secondary",
                                "&:hover": { bgcolor: "action.hover", color: "text.primary" },
                            }}
                        >
                            <FilterList sx={{ fontSize: 18 }} />
                        </SovosBox>
                        <SovosBox
                            sx={{
                                width: 32,
                                height: 32,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                borderRadius: 1,
                                color: "text.secondary",
                                "&:hover": { bgcolor: "action.hover", color: "text.primary" },
                            }}
                        >
                            <ExpandMore
                                sx={{
                                    fontSize: 20,
                                    transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
                                    transition: "transform 150ms",
                                }}
                            />
                        </SovosBox>
                    </SovosBox>
                </SovosBox>

                {rule.exampleFix && !rule.isManual && (
                    <SovosBox
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                            width: "100%",
                            mt: 2,
                            height: 40,
                        }}
                    >
                        <SovosTypography
                            fontWeight={600}
                            sx={{
                                fontSize: 16,
                                lineHeight: "24px",
                                color: "text.primary",
                                whiteSpace: "nowrap",
                            }}
                        >
                            &ldquo;{rule.exampleFix.original_value ?? "—"}&rdquo;
                        </SovosTypography>
                        <SovosBox
                            aria-hidden
                            sx={{
                                flex: 1,
                                position: "relative",
                                height: 2,
                                bgcolor: "text.secondary",
                                opacity: 0.4,
                                "&::after": {
                                    content: '""',
                                    position: "absolute",
                                    right: 0,
                                    top: "50%",
                                    transform: "translateY(-50%)",
                                    width: 0,
                                    height: 0,
                                    borderTop: "5px solid transparent",
                                    borderBottom: "5px solid transparent",
                                    borderLeft: "7px solid",
                                    borderLeftColor: "text.secondary",
                                    opacity: 1,
                                },
                            }}
                        />
                        <SovosTypography
                            fontWeight={600}
                            sx={{
                                fontSize: 16,
                                lineHeight: "24px",
                                color: "text.primary",
                                whiteSpace: "nowrap",
                            }}
                        >
                            &ldquo;{rule.exampleFix.new_value ?? "—"}&rdquo;
                        </SovosTypography>
                    </SovosBox>
                )}

                {rule.isManual && (
                    <SovosTypography
                        sx={{ mt: 2, color: "text.secondary", fontStyle: "italic", fontSize: 14 }}
                    >
                        Manual fix required
                    </SovosTypography>
                )}

                <SovosTypography
                    sx={{
                        mt: 1.5,
                        color: "text.secondary",
                        fontSize: 14,
                        lineHeight: "20px",
                        width: "100%",
                    }}
                >
                    Affecting fixes ({affectingCount})
                </SovosTypography>
            </SovosBox>

            {expanded && (
                <SovosBox sx={{ borderTop: "1px solid", borderColor: "divider", px: 2, py: 1.5, bgcolor: "common.white" }}>
                    <SovosTypography variant="caption" fontWeight={700} sx={{ color: "text.primary", display: "block", mb: 0.75 }}>
                        Pseudo Rule
                    </SovosTypography>
                    <SovosBox
                        sx={{
                            bgcolor: RULE_DETAILS_BG,
                            borderRadius: 1,
                            px: 1.25,
                            py: 1,
                            mb: 2,
                            whiteSpace: "pre-line",
                        }}
                    >
                        <SovosTypography variant="caption" sx={{ color: "text.secondary", fontSize: 11, lineHeight: 1.5 }}>
                            {pseudoRule}
                        </SovosTypography>
                    </SovosBox>

                    <SovosTypography variant="caption" fontWeight={700} sx={{ color: "text.primary", display: "block", mb: 0.75 }}>
                        AI Reasoning
                    </SovosTypography>
                    <SovosTypography variant="caption" sx={{ color: "text.secondary", fontSize: 11, lineHeight: 1.5, display: "block", mb: 2 }}>
                        {rule.rationale || `Suggested normalization for "${rule.columnName}" based on the sampled values in staging.`}
                    </SovosTypography>

                    <SovosBox sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                        <SovosButton
                            size="small"
                            variant="text"
                            onClick={(e) => {
                                e.stopPropagation();
                                onReject(rule.columnName, rule.visibleFixes);
                            }}
                        >
                            Reject
                        </SovosButton>
                        {!rule.isManual && applicableFixCount > 0 && (
                            <SovosButton
                                size="small"
                                variant="contained"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onApprove(rule.columnName, rule.visibleFixes);
                                }}
                            >
                                Apply to {applicableFixCount} row{applicableFixCount !== 1 ? "s" : ""}
                            </SovosButton>
                        )}
                    </SovosBox>
                </SovosBox>
            )}
        </SovosBox>
    );
};

const AiFixPanel = ({
    acceptedFixes,
    aiFixes,
    issueSummary,
    loadingAiFixes,
    onBulkAcceptSafe,
    onBulkApproveColumn,
    onRejectRule,
    onRequestFixes,
    onClose,
    rejectedFixes,
}) => {
    const errorColumns = useMemo(() => deriveErrorColumns(issueSummary), [issueSummary]);
    const [expandedRules, setExpandedRules] = useState(new Set());

    const handleDirectTrigger = useCallback(() => {
        onRequestFixes(errorColumns.length > 0 ? errorColumns : undefined);
    }, [onRequestFixes, errorColumns]);

    const handleToggleRule = useCallback((columnName) => {
        setExpandedRules((prev) => {
            const next = new Set(prev);
            if (next.has(columnName)) next.delete(columnName);
            else next.add(columnName);
            return next;
        });
    }, []);

    const rules = useMemo(
        () => buildRules(aiFixes, acceptedFixes, rejectedFixes),
        [aiFixes, acceptedFixes, rejectedFixes],
    );

    const totalApplicableFixes = useMemo(
        () => rules.reduce((acc, r) => acc + r.pendingFixes.length, 0),
        [rules],
    );

    const hasSuggestions = rules.length > 0;

    return (
        <SovosBox
            sx={{
                width: PANEL_WIDTH,
                flexShrink: 0,
                display: "flex",
                flexDirection: "column",
                height: "100%",
                minHeight: 0,
                alignSelf: "stretch",
                bgcolor: "common.white",
                borderLeft: "1px solid",
                borderColor: "divider",
                overflow: "hidden",
            }}
        >
            <SovosBox
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    height: 48,
                    px: 3,
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    flexShrink: 0,
                    bgcolor: "common.white",
                }}
            >
                <AutoAwesome sx={{ fontSize: 24, color: AI_ACCENT }} />
                <SovosTypography
                    variant="h6"
                    fontWeight={600}
                    sx={{ flex: 1, fontSize: 18, lineHeight: "28px" }}
                >
                    Fix Suggestions{hasSuggestions ? ` (${rules.length})` : ""}
                </SovosTypography>
                {onClose && (
                    <SovosBox
                        onClick={onClose}
                        sx={{
                            width: 32,
                            height: 32,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            borderRadius: 1,
                            cursor: "pointer",
                            color: "text.secondary",
                            "&:hover": { bgcolor: "action.hover", color: "text.primary" },
                        }}
                    >
                        <Close sx={{ fontSize: 20 }} />
                    </SovosBox>
                )}
            </SovosBox>

            <SovosBox
                sx={{
                    flex: 1,
                    overflow: "auto",
                    bgcolor: "grey.50",
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    p: 2,
                }}
            >
                {!hasSuggestions && !loadingAiFixes && (
                    <SovosBox sx={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 1.5, px: 3, py: 3 }}>
                        <AutoAwesome sx={{ fontSize: 40, color: "grey.300" }} />
                        <SovosTypography variant="body2" sx={{ color: "text.secondary", textAlign: "center", px: 2 }}>
                            Sovi will analyse your data errors and suggest corrections.
                        </SovosTypography>
                        {onRequestFixes && (
                            <SovosButton
                                variant="contained"
                                size="small"
                                disabled={loadingAiFixes || errorColumns.length === 0}
                                onClick={handleDirectTrigger}
                                sx={{ mt: 1 }}
                            >
                                {loadingAiFixes ? "Analysing…" : "Suggest corrections"}
                            </SovosButton>
                        )}
                    </SovosBox>
                )}

                {loadingAiFixes && !hasSuggestions && (
                    <SovosBox sx={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 1.5, px: 3, py: 3 }}>
                        <SovosTypography variant="body2" sx={{ color: "text.secondary" }}>Analysing data…</SovosTypography>
                    </SovosBox>
                )}

                {hasSuggestions && rules.map((rule) => (
                    <RuleCard
                        key={rule.columnName}
                        rule={rule}
                        expanded={expandedRules.has(rule.columnName)}
                        onToggle={() => handleToggleRule(rule.columnName)}
                        onApprove={onBulkApproveColumn}
                        acceptedFixes={acceptedFixes}
                        onReject={onRejectRule}
                    />
                ))}
            </SovosBox>

            {hasSuggestions && (
                <SovosBox
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-end",
                        height: 72,
                        px: 3,
                        borderTop: "1px solid",
                        borderColor: "divider",
                        flexShrink: 0,
                        bgcolor: "common.white",
                    }}
                >
                    <SovosButton
                        variant="contained"
                        disabled={totalApplicableFixes === 0}
                        onClick={onBulkAcceptSafe}
                        sx={{ fontWeight: 600, height: 32, px: 2 }}
                    >
                        Apply all rules ({totalApplicableFixes} fix{totalApplicableFixes !== 1 ? "es" : ""})
                    </SovosButton>
                </SovosBox>
            )}
        </SovosBox>
    );
};

export default AiFixPanel;
